<template>
  <div class="search-section">
    <!-- Main Search Row -->
    <div class="search-row">
      <div class="search-input-group">
        <input 
          v-model="searchTerm"
          @keyup.enter="handleSearch"
          @click="handleClearIfCtrlPressed"
          placeholder="RZ, flotila, číslo zak. dealer / MyWay ..."
          class="search-other"
        />
        <button @click="handleSearch" class="search-btn">Hledat</button>
        
        <select 
          v-model="selectedFilter" 
          @change="handleFilterChange" 
          class="filter"
        >
          <option 
            v-for="option in filterOptions" 
            :key="option.value" 
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>
    </div>
    
    <!-- Tire Category Search Row (shows conditionally) -->
    <div class="tire-search-row">
      <TireCategorySearch 
        :fleet-list="fleetList"
        @search="handleCategorySearch"
      />
    </div>
    
    <!-- Debug Info (remove after fixing) -->
    <div v-if="!debugMode" class="debug-info" style="margin-top: 10px; padding: 10px; background: #333; color: #fff; font-size: 12px;">
      <div>Selected Filter: {{ selectedFilter }}</div>
      <div>Is Fleet User: {{ isFleetUser }}</div>
      <div>Fleet List: {{ fleetList }}</div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import TireCategorySearch from './MainTireCategorySearchComponent.vue'

export default {
  name: 'SearchFilters',
  components: { TireCategorySearch },
  props: {
    initialSearchTerm: { type: String, default: '' },
    initialFilter: { type: [String, Number], default: 1 },
    isFleetUser: { type: Boolean, default: false },
    fleetList: { type: Array, default: () => [] }
  },
  emits: ['search', 'filter-change', 'category-search'],
  
  setup(props, { emit }) {
    // Reactive data - convert from data() function
    const searchTerm = ref(props.initialSearchTerm)
    const selectedFilter = ref(props.initialFilter)
    const debugMode = ref(true) // Set to false after debugging

    // Computed properties - convert from computed object
    const filterOptions = computed(() => {
      const baseOptions = [{ value: 1, label: 'Všechno' }]
      
      if (!props.isFleetUser) {
        baseOptions.unshift(
          { value: 2, label: 'Nahrané zakázky' },
          { value: 5, label: 'Archivované zakázky' },
          { value: 6, label: 'Smazané zakázky' },
          { value: 7, label: 'Kategorie pneu' }
        )
      }
      
      return baseOptions
    })

    // Methods - convert to regular functions
    const handleSearch = () => {
      emit('search', { term: searchTerm.value, filter: selectedFilter.value })
    }
    
    const handleFilterChange = () => {
      console.log('Filter changed to:', selectedFilter.value)
      emit('filter-change', selectedFilter.value)
    }
    
    const handleCategorySearch = (data) => {
      console.log('Category search data:', data)
      emit('category-search', data)
    }
    
    const handleClearIfCtrlPressed = (event) => {
      if (event.ctrlKey) {
        searchTerm.value = ''
      }
    }

    // Lifecycle hook - convert mounted() to onMounted()
    onMounted(() => {
      console.log('SearchFilters mounted:', {
        isFleetUser: props.isFleetUser,
        fleetList: props.fleetList,
        selectedFilter: selectedFilter.value
      })
    })

    // Return reactive data and methods for template access
    return {
      searchTerm,
      selectedFilter,
      debugMode,
      filterOptions,
      handleSearch,
      handleFilterChange,
      handleCategorySearch,
      handleClearIfCtrlPressed
    }
  }
}
</script>


<style scoped>
.search-section {
  width: 98%;
  /* Remove fixed height to allow dynamic sizing */
  /* height: 60px; */
  display: flex;
  flex-direction: row;
  gap: 10px;
  padding: 8px;
  border: 1px solid #444;
  background-color: #333;
  border-radius: 5px;
  box-shadow: 0 0 20px rgba(0, 0, 0, .1);
  margin-bottom: 8px;
  justify-content: space-between;
  /* Add align-items to ensure proper vertical alignment */
  align-items: flex-start;
}

.search-row {
  width: fit-content;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-input-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.search-input-group input,
.filter {
  border-radius: 5px;
  border: 1px solid #555;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  padding: 7px;
  color: #fff;
  background-color: #444;
  font-size: 14px;
}

.search-other {
  width: 250px;
  min-width: 200px;
}

.search-input-group input:hover,
.filter:hover {
  border-color: #8bc3ff;
}

.search-input-group input:focus,
.filter:focus {
  outline: none;
  border-color: #8bc3ff;
  box-shadow: 0 0 0 2px rgba(139, 195, 255, 0.25);
}

.search-input-group button {
  border-radius: 5px;
  color: white;
  transition: background-color 0.3s;
  border: none;
  cursor: pointer;
  padding: 7px 20px;
  font-size: 14px;
  white-space: nowrap;
}

.search-btn {
  background-color: #007bff;
}

.search-btn:hover {
  background-color: #0056b3;
}

.filter {
  width: 210px;
  min-width: 150px;
  height: fit-content;
}

.tire-search-row {
  /* Ensure tire search row content stays horizontal */
  display: flex;
  align-items: center;
}

/* Responsive Design */
@media (max-width: 768px) {
  .search-input-group {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input-group input,
  .filter {
    width: 100%;
    margin: 0;
  }
  
  .search-btn {
    align-self: flex-start;
    margin-top: 10px;
  }
}

/* Debug styles */
.debug-info {
  font-family: monospace;
  font-size: 11px;
  line-height: 1.4;
}

.debug-info div {
  margin-bottom: 2px;
}
</style>